// frontend/src/api/playerApi.js
// Player/gear/team roster API calls extracted from DatabaseAPI.js
// Phase 1 Platform Extraction — Step 15B
//
// CONVENTION (see AIDocs/Final/frontend/databaseapi-reference-tdd.md §0):
// EVERY mutating helper (POST/PUT/PATCH/DELETE) must send BOTH
// `...getCsrfHeaders()` and `credentials: 'include'`. Every session-scoped GET
// must send `credentials: 'include'` or it is silently anonymous.

import { API_BASE, getCsrfHeaders } from './transport';
import { handleApiAuthError } from '../auth/authUtils';

export function getTeamData(teamId) {
    if (teamId == undefined) {
        throw new Error("getTeamData: teamId is required and must not be undefined");
    }
    const url = `${API_BASE}/v2/teams/${teamId}/stats/`;

    return fetch(url, {
        credentials: 'include'
    })
        .then(response => {
            if (!response.ok) {
                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }
            }
            return response.json();
        })
        .then(data => {
            console.log('Team Stats Response:', data);
            return data;
        })
        .catch(error => {
            console.error('Error fetching Team stats data:', error);

            if (error.message.includes('Authentication failed')) {
                throw error;
            }

            return null;
        });
}

export function getMatchupData(teamId) {
    return fetch(`${API_BASE}/v2/teams/${teamId}/matchup/`, {
        credentials: 'include'
    })
        .then(response => response.json())
        .then(data => {
            console.log('MAtchup Data:', data);
            return data;
        })
        .catch(error => {
            console.error('Error fetching matchup status:', error);
            return null;
        });
}

export function updatePlayerStatus(teamId, playerId, active) {
    return fetch(`${API_BASE}/v2/teams/${teamId}/roster/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            player_id: playerId,
            active: active
        })
    }).then(response => response.json());
}

export function getCharacterSheet(playerId) {
    return fetch(`${API_BASE}/v2/players/${playerId}/character-sheet/`, {
        credentials: 'include'
    })
        .then(response => response.json());
}

export function updateCharacterSheet(playerId, data) {
    return fetch(`${API_BASE}/v2/players/${playerId}/character-sheet/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(data)
    }).then(response => response.json());
}

/**
 * Swap two players' `active` flags — POST /v2/teams/<id>/swap/.
 *
 * NO CURRENT CALLER. The season roster screen that used this is now a static
 * placeholder (see SeasonTeamPage.js), and the daily TeamPage has never swapped.
 * Kept because the endpoint itself is real and unchanged at baseline.
 *
 * Baseline behaviour (backend/game/api/views/roster.py swap_players): it simply
 * exchanges the two LeaguePlayer.active flags and returns
 * {status, team_id, swapped_players}. There is NO roster lock, NO lineup
 * validation and NO error `code` field — do not write callers that branch on
 * `ROSTER_LOCKED` or `INVALID_LINEUP`; neither exists. The only application-level
 * failure it reports is {'status': 'error', 'message': 'Player not found'} at
 * HTTP 200.
 */
export function swapPlayers(ownerId, player1Name, player2Name) {
    return fetch(`${API_BASE}/v2/teams/${ownerId}/swap/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            player1: player1Name,
            player2: player2Name
        })
    })
    .then(response => {
        // DELIBERATE ASYMMETRY — do not "simplify" this.
        //
        // The endpoint reports application failure as HTTP 200 with a body of
        // {'status': 'error', ...}, so any 2xx body is returned COMPLETELY
        // UNTOUCHED for the caller to inspect.
        //
        // Only a genuine TRANSPORT/AUTH failure (non-2xx) is escalated. Before,
        // those were blindly .json()'d, so a 403/500 HTML page surfaced as an
        // opaque JSON parse error instead of an actionable message.
        if (!response.ok) {
            return response.text().then(bodyText => {
                throw new Error(
                    `swapPlayers: HTTP ${response.status} ${response.statusText} from ` +
                    `POST /v2/teams/${ownerId}/swap/ (player1="${player1Name}", ` +
                    `player2="${player2Name}"). This is a transport/auth failure, NOT the ` +
                    `endpoint's 200-with-status:'error' convention. Response body: ` +
                    `${bodyText || '<empty>'}`
                );
            });
        }
        return response.json();
    });
}

export function getRandomCharacterSheet(position) {
    // Redirect to new league-based endpoint with dummy league ID
    return getRandomAvailablePlayer(1, position);
}

export function getRandomAvailablePlayer(leagueId, position) {
    let url = `${API_BASE}/v2/leagues/${leagueId}/random-player/`;
    if (position) {
        url += `?position=${position}`;
    }
    return fetch(url, {
        credentials: 'include'
    })
        .then(response => response.json())
        .then(data => {
            console.log('Random Available Player:', data);
            return data;
        })
        .catch(error => {
            console.error('Error fetching random available player:', error);
            return null;
        });
}

export function setRoster(teamId, activePlayerIds, benchPlayerIds) {
    return fetch(`${API_BASE}/v2/teams/${teamId}/roster/`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            active_players: activePlayerIds,
            bench_players: benchPlayerIds
        })
    })
    .then(response => response.json())
    .then(data => {
        console.log('Set Roster Response:', data);
        return data;
    })
    .catch(error => {
        console.error('Error setting roster:', error);
        return { status: 'error', message: 'Network error' };
    });
}

export function addPlayerToTeam(teamId, nflPlayerId, position, active = true) {
    return fetch(`${API_BASE}/v2/teams/${teamId}/roster/add/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            nfl_player_id: nflPlayerId,
            position: position,
            active: active
        })
    })
    .then(response => response.json())
    .then(data => {
        console.log('Add Player Response:', data);
        return data;
    })
    .catch(error => {
        console.error('Error adding player to team:', error);
        return { status: 'error', message: 'Network error' };
    });
}

export function removePlayerFromTeam(teamId, leaguePlayerId) {
    return fetch(`${API_BASE}/v2/teams/${teamId}/roster/remove/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            league_player_id: leaguePlayerId
        })
    })
    .then(response => response.json())
    .then(data => {
        console.log('Remove Player Response:', data);
        return data;
    })
    .catch(error => {
        console.error('Error removing player from team:', error);
        return { status: 'error', message: 'Network error' };
    });
}

export function createTeamGear(teamId, gearName, description = '', slot = 'ANY') {
    console.log(`DatabaseAPI: Creating gear "${gearName}" for team ${teamId}`);

    return fetch(`${API_BASE}/v2/gear/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            team: teamId,
            name: gearName,
            description: description,
            slot: slot
        })
    })
    .then(response => {
        console.log(`DatabaseAPI: Create gear response status: ${response.status}`);

        if (!response.ok) {
            console.error(`DatabaseAPI: Gear creation failed with status ${response.status}`);
            throw new Error(`HTTP ${response.status}: Failed to create gear ${gearName}`);
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Gear created successfully:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error creating team gear:', error);
        throw error;
    });
}

export function equipGearToCharacterSheet(gearId, characterSheetId) {
    const url = `${API_BASE}/v2/gear/${gearId}/equip/`;
    const requestBody = { character_sheet_id: characterSheetId };

    console.log(`DatabaseAPI: Attempting to equip gear ${gearId} to character sheet ${characterSheetId}`);
    console.log(`DatabaseAPI: API_BASE = "${API_BASE}"`);
    console.log(`DatabaseAPI: Full URL = "${url}"`);
    console.log(`DatabaseAPI: Request body =`, requestBody);

    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
    })
    .then(response => {
        console.log(`DatabaseAPI: Equip gear response status: ${response.status}`);

        if (!response.ok) {
            console.error(`DatabaseAPI: Gear equip failed with status ${response.status}`);

            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Error response body:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to equip gear`);
            });
        }

        return response.json();
    })
    .catch(error => {
        console.error('DatabaseAPI: Error equipping gear:', error);
        throw error;
    });
}

export function unequipGear(gearId) {
    return fetch(`${API_BASE}/v2/gear/${gearId}/unequip/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to unequip gear`);
        }
        return response.json();
    })
    .catch(error => {
        console.error('Error unequipping gear:', error);
        throw error;
    });
}

/**
 * Fetch a team's full gear inventory.
 *
 * @returns {Promise<Array>} the gear array. REJECTS on any failure.
 *
 * ⚠️ THIS FUNCTION USED TO SWALLOW EVERY FAILURE INTO `[]`. It no longer does,
 * and must never do so again.
 *
 * Both the non-OK branch and the network `.catch()` previously resolved to an
 * empty array, which made a failed read INDISTINGUISHABLE from a genuinely
 * empty inventory. No caller could tell the difference, because there was no
 * difference to observe — a `try/catch` around this call was dead code.
 *
 * The user-visible consequence was the serious part: during an API or auth
 * outage the gear UI confidently reported "No gear equipped" and "All gear is
 * equipped", concealing the manager's real loadout and inviting them to act on
 * a picture of their team that was simply false. An empty inventory is a
 * legitimate state; an unreadable one is not, and the two must never render
 * the same way. (AGENTS.md §1: "Never silent failures … never default, skip,
 * or swallow.")
 *
 * Callers are responsible for catching this and rendering an explicit error
 * state. An empty array from here now means, truthfully, that the team owns
 * no gear.
 */
export function getTeamGear(teamId) {
    const url = `${API_BASE}/v2/teams/${teamId}/gear/`;

    return fetch(url, {
        credentials: 'include'
    })
        .then(res => {
            if (!res.ok) {
                throw new Error(
                    `HTTP ${res.status}: Failed to load team gear for team ${teamId}. ` +
                    'The gear inventory could not be read, so the current loadout is ' +
                    'UNKNOWN — it must not be rendered as empty.'
                );
            }
            return res.json();
        })
        .catch(error => {
            console.error(`Error fetching team gear for team ${teamId}:`, error);
            throw error;
        });
}

/**
 * Gear-adjusted PROJECTED stat line + points for ONE rostered player.
 *
 *   GET /api/v2/teams/{teamId}/players/{characterSheetId}/weekly-projection/
 *
 * Contract: AIDocs/Final/backend/api-reference-tdd.md §2.4c.
 * The weekly endpoint reads NFLProjectionMirror.projected_stats for the
 * owning league's current season/week, scores that stat line through the
 * existing canonical Mythic scoring engine with and without the player's
 * equipped gear, and returns both stat lines plus per-item attribution.
 * The client only formats the server response and computes
 * geared_points - base_points; it does not calculate gear effects.
 *
 * @param {number|string} teamId
 * @param {number|string} characterSheetId - CharacterSheet PK, never the
 *        LeaguePlayer PK (which identifies a different database table).
 * @param {Object} [options]
 * @param {Array<number>|undefined} [options.gearIds] - absent uses currently
 *        equipped gear; [] requests an explicitly empty loadout.
 * @returns {Promise<Object>} parsed weekly projection response; rejects on
 *          transport errors, invalid input, or unexpected response contracts.
 *
 * Only gearIds is supported. Historical sourceYear/sourceWeek are NOT accepted
 * by the weekly API; callers must not request another season/week.
 *
 * Missing NFLProjectionMirror rows, projected_stats, or projection_source can
 * return HTTP 200 with available:false and a reason; missing required scoring
 * inputs can also be unavailable. A generic projected 2PT count without
 * attribution may instead return an explicitly PARTIAL numeric result.
 * Never coerce unavailable to zero or present partial scoring as complete.
 *
 * A 403 means this owner-only projection cannot be read by this user. Do not
 * pass that to handleApiAuthError(), which treats it as session expiry and
 * could log someone out merely for viewing an opponent.
 *
 * gearIds omitted vs [] is deliberate: an omitted query parameter loads the
 * persisted current equipment; an empty parameter requests a known empty set.
 * The serializer must keep the difference.
 */
export function getPlayerGearProjection(teamId, characterSheetId, options = {}) {
    if (teamId == null) {
        throw new Error(
            'getPlayerGearProjection: teamId is required. Received ' +
            JSON.stringify(teamId) + '.'
        );
    }
    if (characterSheetId == null) {
        throw new Error(
            'getPlayerGearProjection: characterSheetId is required. Received ' +
            JSON.stringify(characterSheetId) + '. A projection cannot be ' +
            'attributed without a CharacterSheet id, and player.id is a ' +
            'DIFFERENT table\'s key — do not substitute it.'
        );
    }

    if (Object.prototype.hasOwnProperty.call(options, 'sourceYear') ||
        Object.prototype.hasOwnProperty.call(options, 'sourceWeek')) {
        throw new Error(
            'getPlayerGearProjection: historical sourceYear/sourceWeek are not ' +
            'supported by weekly NFL projections.'
        );
    }
    const { gearIds = undefined } = options;

    const params = new URLSearchParams();
    // `undefined`/`null` → parameter omitted entirely (project equipped gear).
    // `[]` → `gear_ids=` (project an explicitly empty loadout).
    if (Array.isArray(gearIds)) params.set('gear_ids', gearIds.join(','));

    const query = params.toString();
    const url =
        `${API_BASE}/v2/teams/${teamId}/players/${characterSheetId}/weekly-projection/` +
        (query ? `?${query}` : '');

    return fetch(url, { credentials: 'include' })
        .then((response) => {
            if (!response.ok) {
                return response.text().then((bodyText) => {
                    const error = new Error(
                        `getPlayerGearProjection: HTTP ${response.status} ` +
                        `${response.statusText} from GET ${url}. The projection is ` +
                        'UNKNOWN and must not be rendered as "no projection data". ' +
                        `Response body: ${bodyText || '<empty>'}`
                    );
                    // Read by the adapter to tell the owner-only boundary (403)
                    // apart from a genuine failure. Not used for auth handling.
                    error.status = response.status;
                    throw error;
                });
            }
            return response.json();
        })
        .then((data) => {
            // A 200 WITH THE WRONG SHAPE IS A CONTRACT VIOLATION, NOT MISSING
            // DATA. `available` is the field every other branch keys off, so a
            // body without it cannot be interpreted at all — degrading it to
            // "unavailable" would hide a serialiser regression behind a normal
            // missing-weekly-projection state.
            if (!data || typeof data.available !== 'boolean') {
                throw new Error(
                    `getPlayerGearProjection: GET ${url} returned HTTP 200 but no ` +
                    'boolean `available` field (received ' + JSON.stringify(data) +
                    '). This is an API contract violation, not a missing projection.'
                );
            }
            return data;
        })
        .catch((error) => {
            console.error(
                `Error fetching gear projection for team ${teamId}, character ` +
                `sheet ${characterSheetId}:`,
                error
            );
            throw error;
        });
}

export function getPlayerEquipment(playerId) {
    const url = `${API_BASE}/v2/players/${playerId}/equipment/`;

    return fetch(url, {
        credentials: 'include'
    })
        .then(res => {
            if (res.ok) {
                return res.json();
            } else {
                return {
                    character_sheet_id: playerId,
                    equipment: {
                        HEAD: null,
                        CHEST: null,
                        HANDS: null,
                        LEGS: null,
                        FEET: null,
                        ANY: []
                    },
                    total_equipped: 0
                };
            }
        })
        .catch(error => {
            console.error('Error fetching player equipment:', error);
            return {
                character_sheet_id: playerId,
                equipment: {
                    HEAD: null,
                    CHEST: null,
                    HANDS: null,
                    LEGS: null,
                    FEET: null,
                    ANY: []
                },
                total_equipped: 0
            };
        });
}

export function markGearUsed(gearId) {
    return fetch(`${API_BASE}/v2/gear/${gearId}/mark-used/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to mark gear as used`);
        }
        return response.json();
    })
    .catch(error => {
        console.error('Error marking gear as used:', error);
        throw error;
    });
}
